"use client";

import { Button } from "@/components/ui/button";
import {
  formatBytes,
  useFileUpload,
  type FileWithPreview,
} from "@/hooks/use-file-upload";
import { cn } from "@/lib/utils";
import { Camera, CheckCircle2, IdCard, UploadCloud, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface DocumentCardUploadProps {
  label: string;
  sublabel?: string;
  value?: File | string | null;
  onFileChange?: (file: FileWithPreview | null) => void;
  maxSize?: number;
  className?: string;
  disabled?: boolean;
}

export function DocumentCardUpload({
  label,
  sublabel,
  value,
  onFileChange,
  maxSize = 8 * 1024 * 1024, // 8MB
  className,
  disabled = false,
}: DocumentCardUploadProps) {
  const { t } = useTranslation("guestForm");
  const [
    { files, isDragging },
    {
      removeFile,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      getInputProps,
      handleFileChange,
    },
  ] = useFileUpload({
    maxFiles: 1,
    maxSize,
    accept: "image/*,application/pdf",
    multiple: false,
    onFilesChange: (files) => {
      onFileChange?.(files[0] || null);
    },
  });

  const currentFile = files[0];
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value && currentFile) {
      removeFile(currentFile.id);
      setPreviewUrl(null);
    } else if (value instanceof Blob) {
      setPreviewUrl(URL.createObjectURL(value));
    } else if (typeof value === "string" && value.length > 0) {
      setPreviewUrl(value);
    } else if (!value) {
      setPreviewUrl(null);
    }
  }, [value]);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentFile) {
      removeFile(currentFile.id);
    }
    setPreviewUrl(null);
    onFileChange?.(null);
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileWithPreview: FileWithPreview = {
        id: Math.random().toString(36).substring(7),
        file,
        preview: URL.createObjectURL(file),
      };
      setPreviewUrl(fileWithPreview.preview);
      onFileChange?.(fileWithPreview);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2 w-full", className)}>
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <IdCard className="size-4 text-primary" />
            {label}
          </span>
          {sublabel && (
            <p className="text-xs text-muted-foreground">{sublabel}</p>
          )}
        </div>
        {previewUrl && (
          <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <CheckCircle2 className="size-3.5" />
            {t("documentTab.uploaded", "Caricato")}
          </span>
        )}
      </div>

      <div
        className={cn(
          "relative group w-full aspect-[1.586] rounded-xl border-2 border-dashed transition-all duration-200 overflow-hidden flex flex-col items-center justify-center p-3 text-center cursor-pointer select-none",
          isDragging
            ? "border-primary bg-primary/10 scale-[1.01]"
            : previewUrl
              ? "border-emerald-500/50 bg-emerald-50/20 dark:bg-emerald-950/10"
              : "border-muted-foreground/30 hover:border-primary/70 hover:bg-muted/30 bg-muted/10",
          disabled && "opacity-50 pointer-events-none",
        )}
        onClick={!disabled ? openFileDialog : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input {...getInputProps()} className="sr-only" disabled={disabled} />

        {/* Input dedicato per scatto foto diretta con fotocamera */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={handleCameraCapture}
          disabled={disabled}
        />

        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt={label}
              className="absolute inset-0 w-full h-full object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-8 text-xs shadow-md"
                onClick={(e) => {
                  e.stopPropagation();
                  openFileDialog();
                }}
              >
                {t("documentTab.change", "Cambia")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="h-8 text-xs shadow-md"
                onClick={handleRemove}
              >
                {t("documentTab.remove", "Rimuovi")}
              </Button>
            </div>
            <Button
              type="button"
              size="icon"
              variant="destructive"
              className="size-7 absolute top-2 right-2 rounded-full shadow-md md:hidden"
              onClick={handleRemove}
            >
              <X className="size-4" />
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-center p-2">
            <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <UploadCloud className="size-5" />
            </div>
            <div className="space-y-0.5">
              <p className="text-xs font-semibold text-foreground">
                {t("documentTab.tapToUpload", "Tocca per caricare o trascina")}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {t("documentTab.fileTypes", {
                  size: formatBytes(maxSize),
                  defaultValue: `JPG, PNG fino a ${formatBytes(maxSize)}`,
                })}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2.5 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  cameraInputRef.current?.click();
                }}
              >
                <Camera className="size-3.5 mr-1" />
                {t("documentTab.camera", "Fotocamera")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
